// aiWorkoutSmart.js — derives smart defaults for the AI Workout
// Generator modal so the user lands on the right preset instead of
// having to think about Goal / Duration / Equipment from scratch.
//
// Pure functions over readiness + workout history + time-of-day. No
// React, no DOM access — the modal calls these on open and renders
// the result.

const GOAL_MUSCLES = {
  "Upper Body":    ["Chest","Back","Shoulders","Triceps","Biceps"],
  "Push":          ["Chest","Shoulders","Triceps"],
  "Pull":          ["Back","Biceps"],
  "Lower Body":    ["Legs","Glutes"],
  "Legs":          ["Legs","Glutes"],
  "Cardio & Core": ["Core","Cardio"],
  "Full Body":     ["Chest","Back","Shoulders","Legs","Glutes","Triceps","Biceps","Core"],
};

const ALL_GOALS = ["Full Body","Upper Body","Lower Body","Push","Pull","Legs","Cardio & Core"];

// Logged `muscles` strings use finer-grained names than the GOAL_MUSCLES
// groups (e.g. "Quads", "Lats", "Rear delts"). Fold them onto the canonical
// training groups so weekly set counts line up with volume landmarks.
const MUSCLE_CANON = {
  Chest:"Chest", Pecs:"Chest",
  Back:"Back", Lats:"Back", Traps:"Back", "Upper back":"Back",
  Shoulders:"Shoulders", Delts:"Shoulders", "Rear delts":"Shoulders", "Front delts":"Shoulders",
  Biceps:"Biceps", Forearms:"Biceps",
  Triceps:"Triceps",
  Legs:"Legs", Quads:"Legs", Hamstrings:"Legs", Calves:"Legs", Adductors:"Legs", Abductors:"Legs",
  Glutes:"Glutes",
  Core:"Core", Abs:"Core", Obliques:"Core",
  Cardio:"Cardio", "Full body":"Full body",
};
function canonMuscle(m) { return MUSCLE_CANON[m] || m; }

// Hypertrophy volume landmarks (sets per muscle per week, Renaissance
// Periodization heuristics): below MEV the muscle is under-stimulated and
// should be prioritised; MEV..MAV is the productive band; above MRV we back
// off to avoid junk volume / overreaching.
const VOL_MEV = 10;  // minimum effective volume
const VOL_MRV = 20;  // maximum recoverable volume

// Count hard sets per canonical muscle group over the last `days` days.
// A "hard set" = any logged working set (reps > 0). Primary muscle gets full
// credit; secondary muscles get half credit (standard fractional-volume
// convention). Returns { [muscle]: setsThisWeek }.
export function weeklySetVolume(history, days = 7) {
  const cutoff = Date.now() - days * 86400000;
  const out = {};
  for (const w of (history || [])) {
    const ts = workoutTs(w);
    if (!ts || ts < cutoff) continue;
    for (const ex of (w.exercises || [])) {
      const workingSets = (ex.sets || []).filter(s => (parseInt(s.reps, 10) || 0) > 0).length;
      if (!workingSets) continue;
      const muscles = String(ex.muscles || ex.primary || "")
        .split(",").map(s => canonMuscle(s.trim())).filter(Boolean);
      muscles.forEach((m, i) => {
        const credit = i === 0 ? 1 : 0.5; // primary full, secondary half
        out[m] = (out[m] || 0) + workingSets * credit;
      });
    }
  }
  for (const k of Object.keys(out)) out[k] = Math.round(out[k] * 10) / 10;
  return out;
}

// Per-muscle volume status against the landmarks above.
// status: "under" (< MEV, prioritise) | "productive" (MEV..MRV) | "over" (> MRV).
export function volumeStatus(history, days = 7) {
  const vol = weeklySetVolume(history, days);
  const out = {};
  for (const m of ["Chest","Back","Shoulders","Biceps","Triceps","Legs","Glutes","Core"]) {
    const sets = vol[m] || 0;
    out[m] = {
      sets,
      status: sets < VOL_MEV ? "under" : sets > VOL_MRV ? "over" : "productive",
      // deficit below the productive floor (0 if at/above MEV) — positive = needs work
      deficit: Math.max(0, VOL_MEV - sets),
      surplus: Math.max(0, sets - VOL_MRV),
    };
  }
  return out;
}

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function workoutTs(w) {
  return w.timestamp || w.completedAt || w.startedAt
    || (w.date ? Date.parse(`${w.date}T12:00:00`) : 0);
}

// Build "days since last trained" per muscle group from workout history.
// Looks back 14 days. Anything older returns ∞ (treated as "fresh enough").
export function muscleStaleness(history, lookbackDays = 14) {
  const now = Date.now();
  const lookbackMs = lookbackDays * 86400000;
  const lastSeen = {}; // muscle -> ts
  for (const w of (history || [])) {
    const ts = workoutTs(w);
    if (!ts || (now - ts) > lookbackMs) continue;
    for (const ex of (w.exercises || [])) {
      const muscles = String(ex.muscles || ex.primary || "")
        .split(",").map(s => s.trim()).filter(Boolean);
      for (const m of muscles) {
        if (!lastSeen[m] || ts > lastSeen[m]) lastSeen[m] = ts;
      }
    }
  }
  const out = {};
  for (const [m, ts] of Object.entries(lastSeen)) {
    out[m] = Math.round((now - ts) / 86400000); // days ago
  }
  return out;
}

// For each goal, summarise the muscle staleness it would target.
// Returns { goal: { freshDays, stalest, freshest, isStale } }.
// freshDays = average days since each constituent muscle was trained
// (untouched in 14d counts as 14). "isStale" flags goals where the
// average is ≥4d — those are the ones we should bias toward.
export function goalStalenessMap(history) {
  const stale = muscleStaleness(history, 14);
  const out = {};
  for (const g of ALL_GOALS) {
    const muscles = GOAL_MUSCLES[g];
    const perMuscle = muscles.map(m => ({
      muscle: m,
      daysAgo: stale[m] != null ? stale[m] : 14, // "never in 14d" => treat as 14
      seen: stale[m] != null,
    }));
    const seenMuscles = perMuscle.filter(p => p.seen);
    const avgFresh = perMuscle.reduce((s, p) => s + p.daysAgo, 0) / perMuscle.length;
    // The "freshest" (most recently trained) muscle in the group — the
    // one we'd risk overtraining if we picked this goal.
    const freshest = seenMuscles.length
      ? seenMuscles.reduce((min, p) => p.daysAgo < min.daysAgo ? p : min)
      : null;
    const stalest = perMuscle.reduce((max, p) => p.daysAgo > max.daysAgo ? p : max);
    out[g] = {
      avgDaysAgo: +avgFresh.toFixed(1),
      freshest, // { muscle, daysAgo } — null if none trained in 14d
      stalest,  // { muscle, daysAgo }
      perMuscle,
      isStale: avgFresh >= 4,
      isFresh: freshest && freshest.daysAgo <= 1, // risk of overtrain
    };
  }
  return out;
}

// Score a goal by how much its constituent muscles NEED work this week,
// from the weekly volume landmarks. Under-MEV muscles add to the score
// (prioritise), over-MRV muscles subtract (back off). Cardio/Full-body
// constituents not in the landmark set contribute 0.
function goalVolumeNeed(goal, vstatus) {
  const muscles = GOAL_MUSCLES[goal] || [];
  let score = 0, counted = 0;
  for (const m of muscles) {
    const s = vstatus[m];
    if (!s) continue;
    counted++;
    score += s.deficit;        // how far under the productive floor
    score -= s.surplus * 0.5;  // penalise piling onto an over-served muscle
  }
  return counted ? score / counted : 0; // average per targeted muscle
}

// Suggest a goal from volume landmarks + staleness + readiness. Algorithm:
// - REST → return null (caller shows REST flow instead).
// - EASY → prefer Cardio & Core (low CNS load). [unchanged]
// - HARD/MEDIUM → among goals that pass the overtrain guard (freshest
//   muscle ≥2d ago), pick the one whose muscles are MOST under their
//   weekly volume target — i.e. both stale AND under-trained — not recency
//   alone. Ties break toward greater staleness, then higher average rest.
export function suggestGoal(readinessClass, history) {
  if (readinessClass === "REST") return null;
  const map = goalStalenessMap(history);
  if (readinessClass === "EASY") return "Cardio & Core";

  const vstatus = volumeStatus(history);

  // Filter out goals whose freshest muscle is < 2 days ago (overtrain risk)
  let pool = ALL_GOALS.filter(g => {
    const info = map[g];
    if (!info.freshest) return true; // nothing trained in 14d, safe
    return info.freshest.daysAgo >= 2;
  });
  // If nothing qualifies (very busy week), allow >= 1 day
  if (!pool.length) pool = ALL_GOALS.filter(g => {
    const info = map[g];
    return !info.freshest || info.freshest.daysAgo >= 1;
  });
  // Last resort
  if (!pool.length) pool = ALL_GOALS;

  // Pick by (volume need desc, then freshest.daysAgo desc, then avg desc).
  // Volume need dominates: train what's under-stimulated AND rested. The
  // staleness terms only break ties between similarly under-trained goals.
  pool.sort((a, b) => {
    const needA = goalVolumeNeed(a, vstatus);
    const needB = goalVolumeNeed(b, vstatus);
    if (Math.abs(needB - needA) > 0.5) return needB - needA;
    const A = map[a], B = map[b];
    const af = A.freshest?.daysAgo ?? 14;
    const bf = B.freshest?.daysAgo ?? 14;
    if (bf !== af) return bf - af;
    return B.avgDaysAgo - A.avgDaysAgo;
  });
  return pool[0];
}

// Learn exercise preferences from the feedback captured at session finish
// (plannedExerciseIds / skippedExercises / swappedExercises). Returns:
//   { avoid: [id], prefer: [id], stats: { [id]: { planned, skipped, swappedIn } } }
// - avoid:  exercises the user keeps bailing on — planned ≥ minPlanned times
//   and skipped ≥ skipRate of those times. The generator drops these.
// - prefer: exercises the user repeatedly swaps IN (≥ minSwap times) — not in
//   the plan but added during the session. The generator biases toward these.
// Pure over the workout history; safe on empty/legacy data (older sessions
// simply have no feedback fields and contribute nothing).
export function exercisePreferences(history, {
  lookbackDays = 60, minPlanned = 3, skipRate = 0.6, minSwap = 2,
} = {}) {
  const cutoff = Date.now() - lookbackDays * 86400000;
  const stats = {};
  const bump = (id, key) => {
    if (!id) return;
    (stats[id] = stats[id] || { planned: 0, skipped: 0, swappedIn: 0 })[key]++;
  };
  for (const w of (history || [])) {
    const ts = workoutTs(w);
    if (!ts || ts < cutoff) continue;
    for (const id of (w.plannedExerciseIds || [])) bump(id, "planned");
    for (const s of (w.skippedExercises || [])) bump(s?.id, "skipped");
    for (const id of (w.swappedExercises || [])) bump(id, "swappedIn");
  }
  const avoid = [], prefer = [];
  for (const [id, s] of Object.entries(stats)) {
    if (s.planned >= minPlanned && s.skipped / s.planned >= skipRate) avoid.push(id);
    if (s.swappedIn >= minSwap) prefer.push(id);
  }
  return { avoid, prefer, stats };
}

// Suggest a duration. HARD = 60m default, MEDIUM = 45m, EASY = 30m.
// REST → null. Late evening (>= 21h local) trims 15min off any non-REST
// recommendation to encourage a quicker session before wind-down.
export function suggestDuration(readinessClass, nowHour = new Date().getHours()) {
  if (readinessClass === "REST") return null;
  let base;
  if (readinessClass === "HARD") base = 60;
  else if (readinessClass === "EASY") base = 30;
  else base = 45;
  if (nowHour >= 21 || nowHour < 5) base = Math.max(30, base - 15);
  return base;
}

// Suggest equipment based on the user's last 30 days of logged workouts.
// Returns { equipment, last30: { "Full Gym": n, "Dumbbells Only": n, "Bodyweight": n } }.
// Falls back to "Full Gym" if no history.
export function suggestEquipment(history) {
  const now = Date.now();
  const cutoff = now - 30 * 86400000;
  const last30 = { "Full Gym": 0, "Dumbbells Only": 0, "Bodyweight": 0 };

  for (const w of (history || [])) {
    const ts = workoutTs(w);
    if (!ts || ts < cutoff) continue;
    // Best-effort detection from exercise categories. The workout schema
    // doesn't track equipment-mode at session level, so infer from the
    // exercise mix: any heavy barbell = Full Gym, only dumbbell/bodyweight
    // = Dumbbells Only or Bodyweight.
    let hasBarbell = false, hasDumbbell = false, onlyBodyweight = true;
    for (const ex of (w.exercises || [])) {
      const name = String(ex.name || "").toLowerCase();
      if (/(barbell|machine|cable|smith|rack|pulldown|leg press)/.test(name)) hasBarbell = true;
      if (/(dumbbell|kettlebell|db\b)/.test(name)) hasDumbbell = true;
      const muscles = String(ex.muscles || "").toLowerCase();
      if (hasBarbell || hasDumbbell || /(weight|kg)/.test(muscles)) onlyBodyweight = false;
    }
    if (hasBarbell) last30["Full Gym"]++;
    else if (hasDumbbell) last30["Dumbbells Only"]++;
    else if (onlyBodyweight) last30["Bodyweight"]++;
    else last30["Full Gym"]++; // ambiguous → assume Full Gym
  }
  const sorted = Object.entries(last30).sort((a, b) => b[1] - a[1]);
  const equipment = sorted[0][1] > 0 ? sorted[0][0] : "Full Gym";
  return { equipment, last30 };
}

// Rough estimate for the pre-flight preview. Based on duration + goal
// type + readiness (HARD uses higher intensity). Returns:
//   { exerciseCount, estEffortMin, estBurnKcal, blockCount }
// These are heuristics for the user's expectation-setting, not actual
// physiological predictions.
export function previewEstimate({ duration, goal, readinessClass, weightKg = 80, exerciseCount: actualCount }) {
  // Pre-generation we assume 7 (the generator's target); post-generation the
  // caller passes the ACTUAL count so the card reflects the real workout.
  const exerciseCount = actualCount > 0 ? actualCount : 7;
  const blockCount = Math.ceil(exerciseCount / 3); // A/B/C blocks
  // Effort min ≈ duration - warmup/rest deadtime (~25%)
  const estEffortMin = Math.round(duration * 0.75);
  // Burn kcal: rough MET-based estimate.
  //   Cardio & Core ≈ 7 METs, strength ≈ 5 METs, light/EASY ≈ 4 METs.
  let mets = 5;
  if (goal === "Cardio & Core") mets = 7;
  if (readinessClass === "EASY") mets = 4;
  if (readinessClass === "HARD") mets += 0.5;
  const estBurnKcal = Math.round(mets * weightKg * (duration / 60));
  return { exerciseCount, blockCount, estEffortMin, estBurnKcal };
}

// Rough cost estimate for the AI call. The generator uses Flash-lite
// at $0.10 in / $0.40 out per 1M tokens. Typical payload ~1.5–2k in,
// 600–1200 out. Returns a USD figure to show inline before the user
// taps Generate.
export function estimateGenerationCostUSD() {
  // Conservative midpoints to avoid under-promising.
  const inTok = 2000;
  const outTok = 1000;
  // gemini-2.5-flash-lite rates (see hayah/aiConfig.js)
  return (inTok / 1_000_000) * 0.10 + (outTok / 1_000_000) * 0.40;
}

// Should we surface a "log pre-workout fuel" CTA inside the modal?
// True when readiness is HARD/MEDIUM AND today's carbs < 30g AND last
// meal was >4h ago (or never). These signal a fasted/under-fuelled
// state going into a non-trivial session.
export function shouldShowFuelReminder(readinessInputs) {
  if (!readinessInputs) return false;
  const cls = readinessInputs.classification;
  if (cls === "EASY" || cls === "REST") return false;
  const carbs = readinessInputs.nutrition?.todayCarbs ?? 0;
  const since = readinessInputs.nutrition?.hoursSinceLastMeal;
  const fasted = readinessInputs.fasting?.currentlyFasted;
  if (carbs >= 30) return false;
  if (since != null && since < 4) return false;
  return true || fasted; // either way, low fuel + hard day = nudge
}
